import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { NotificationRef } from '../../../core/generated';
import { NotificationRefControllerService } from '../../../core/generated/api/notificationRefController.service';
import { ContainerComponent } from '../../../shared/container/container.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-admin-notification-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">{{ data.notification ? 'Editar Notificação' : 'Nova Notificação' }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <form [formGroup]="notificationForm">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Título</mat-label>
                <input matInput formControlName="title" />
                <mat-error *ngIf="notificationForm.get('title')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Mensagem</mat-label>
                <textarea matInput formControlName="message" rows="4"></textarea>
                <mat-error *ngIf="notificationForm.get('message')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-checkbox formControlName="global">Notificação Global</mat-checkbox>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>E-mail</mat-label>
                <input matInput formControlName="email" type="email" />
                <mat-error *ngIf="notificationForm.get('email')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>
            </form>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onCancel()">
              <mat-icon>close</mat-icon> Cancelar
            </button>
            <button mat-flat-button color="primary" class="btn btn-primary" (click)="onSave()" [disabled]="notificationForm.invalid">
              <mat-icon>save</mat-icon> Salvar
            </button>
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    mat-form-field { 
      width: 100%; 
      margin-bottom: var(--spacing-sm); 
    }
    mat-checkbox {
      margin-bottom: var(--spacing-md);
    }
  `]
})
export class AdminNotificationDialogComponent {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AdminNotificationDialogComponent>);

  notificationForm = this.fb.group({
    id: [null as number | null],
    title: ['', Validators.required],
    message: ['', Validators.required],
    sentAt: [null as string | null],
    global: [false],
    email: ['', Validators.required],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { notification?: NotificationRef }) {
    if (this.data.notification) {
      this.notificationForm.patchValue({
        id: this.data.notification.id ?? null,
        title: this.data.notification.title ?? '',
        message: this.data.notification.message ?? '',
        sentAt: this.data.notification.sentAt ?? null,
        global: this.data.notification.global ?? false,
        email: this.data.notification.email ?? '',
      });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.notificationForm.valid) {
      const notificationData: NotificationRef = {
        id: this.notificationForm.value.id ? Number(this.notificationForm.value.id) : undefined,
        title: this.notificationForm.value.title || '',
        message: this.notificationForm.value.message || '',
        sentAt: new Date().toISOString(),
        global: this.notificationForm.value.global || false,
        email: this.notificationForm.value.email || '',
      };
      this.dialogRef.close(notificationData);
    }
  }
}

@Component({
    selector: 'app-admin-notification',
    standalone: true,
    imports: [MaterialModule, ContainerComponent],
    templateUrl: './admin-notification.component.html',
    styleUrl: './admin-notification.component.scss'
})
export class AdminNotificationComponent implements OnInit, OnDestroy {
  _snackBar = inject(MatSnackBar);
  cd = inject(ChangeDetectorRef);
  notificationService = inject(NotificationRefControllerService);
  dialog = inject(MatDialog);

  isLoading = false;
  private notificationSubscription?: Subscription;
  notifications: NotificationRef[] = [];

  async ngOnInit() {
    await this.loadNotifications();
  }

  ngOnDestroy() {
    this.notificationStreamCleanSubscriptions();
  }

  loadNotifications() {
    this.notificationService.findAll7().subscribe({
      next: (response) => {
        if (!response || !response.content) {
          this.notifications = [];
          return;
        }
        this.notifications = (response.content as NotificationRef[]).sort(
          (a, b) =>
            new Date(b.sentAt || '').getTime() -
            new Date(a.sentAt || '').getTime()
        );
        this.cd.detectChanges();
      }
    });
  }

  openNotificationDialog(notification?: NotificationRef) {
    const dialogRef = this.dialog.open(AdminNotificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { notification }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.saveNotification(result);
      }
    });
  }

  editNotification(notification: NotificationRef) {
    this.openNotificationDialog(notification);
  }

  async deleteNotification(id: number) {
    if (!confirm('Deseja realmente excluir esta notificação?')) {
      return;
    }

    try {
      await firstValueFrom(
        this.notificationService.deleteById11(Number(id))
      );
      this.notifications = this.notifications.filter(
        (item) => item.id !== Number(id)
      );
      this._snackBar.open('Notificação excluída com sucesso!', 'Fechar', {
        duration: 5000,
      });
      this.cd.detectChanges();
    } catch (error) {
      this._snackBar.open('Erro ao excluir notificação', 'Fechar', {
        duration: 5000,
      });
    }
  }

  async saveNotification(body: NotificationRef) {
    try {
      if (body.id !== undefined && body.id !== null) {
        const updated = await firstValueFrom(
          this.notificationService.update11(body.id, body)
        );
        const index = this.notifications.findIndex((n) => n.id === updated.id);
        if (index !== -1) {
          this.notifications[index] = updated;
        }
        this._snackBar.open('Notificação atualizada com sucesso!', 'Fechar', {
          duration: 5000,
        });
      } else {
        const created = await firstValueFrom(
          this.notificationService.save11(body)
        );
        this.notifications.unshift(created);
        this._snackBar.open('Notificação criada com sucesso!', 'Fechar', {
          duration: 5000,
        });
      }

      this.notifications.sort(
        (a, b) =>
          new Date(b.sentAt || '').getTime() -
          new Date(a.sentAt || '').getTime()
      );
      this.cd.detectChanges();
      await this.loadNotifications();
    } catch (error) {
      this._snackBar.open('Erro ao salvar notificação', 'Fechar', {
        duration: 5000,
      });
    }
  }

  notificationStreamCleanSubscriptions() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = undefined;
    }
    this.notificationService.cleanupInactiveStreams().subscribe({
      next: () => {
        console.log('Subscriptions limpas com sucesso');
        this._snackBar.open('Subscriptions limpas com sucesso!', 'Fechar', {
          duration: 3000,
        });
      },
      error: (error) => {
        console.error('Erro ao limpar subscriptions:', error);
        this._snackBar.open('Erro ao limpar subscriptions', 'Fechar', {
          duration: 3000,
        });
      }
    });
  }

  notificationStreamSubscriptions() {
    this.notificationSubscription = this.notificationService
      .getRegisteredStreams()
      .subscribe({
        next: (response) => {
          console.log('Lista dos subscritos:', response);
        },
        complete: () => {
          console.log('Notification stream completed');
        },
      });
  }
}
